import { useCallback, useMemo, useState, createElement } from 'react';
import { ChevronRight } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { OwnerAvatar } from '@/shared/components/OwnerAvatar';
import type { DataTableColumn } from '@/shared/components/data-table';
import { isPopulatedUser } from '../types/qms.types';
import type { QmsTicket } from '../types/qms.types';

export interface QmsTicketRenderContext {
  /* Empty for now — assignee/department labels are derived from the ticket itself. */
  _: undefined;
}

export type QmsTicketColumnDef = DataTableColumn<QmsTicket, QmsTicketRenderContext>;

const ALL_COLUMN_KEYS = [
  'ticket_number',
  'title',
  'type',
  'status',
  'priority',
  'department',
  'assignee',
  'due_date',
  'actions',
];
const HIDDEN_BY_DEFAULT = new Set<string>();

const VISIBILITY_KEY = 'atek-qms-list-column-visibility';
const FILTERS_KEY = 'atek-qms-list-column-filters';

const TYPE_LABELS: Record<string, string> = {
  ticket: 'Ticket',
  capa: 'CAPA',
  client: 'Client',
};

const TYPE_PREFIX: Record<string, string> = {
  ticket: 'QUAL',
  capa: 'CAPA',
  client: 'CLT',
};

const STATUS_LABELS: Record<string, string> = {
  submitted: 'Submitted',
  in_progress: 'In Progress',
  ready_to_test: 'Ready to Test',
  complete: 'Complete',
  validated_closed: 'Validated/Closed',
  pending_review: 'Pending Review',
  approved: 'Approved',
  closed: 'Closed',
};

const DEPARTMENT_LABELS: Record<string, string> = {
  product: 'Product',
  sales_marketing: 'Sales & Marketing',
  finance: 'Finance',
  it: 'IT',
  hr: 'HR',
  quality: 'Quality',
  operations: 'Operations',
};

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const key of ALL_COLUMN_KEYS) {
    defaults[key] = !HIDDEN_BY_DEFAULT.has(key);
  }
  return defaults;
}

function loadVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return getDefaultVisibility();
}

function loadColumnFilters(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}');
  } catch { return {}; }
}

function getAssigneeName(ticket: QmsTicket): string {
  if (isPopulatedUser(ticket.assigned_to)) {
    const u = ticket.assigned_to;
    return `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim() || u.email || 'Unassigned';
  }
  return 'Unassigned';
}

function getAssigneeId(ticket: QmsTicket): string | null {
  if (isPopulatedUser(ticket.assigned_to)) return ticket.assigned_to._id;
  return null;
}

function buildColumns(
  assigneeOptions: { label: string; value: string }[],
  departmentOptions: { label: string; value: string }[],
): QmsTicketColumnDef[] {
  return [
    {
      key: 'ticket_number',
      label: 'Ticket #',
      sortKey: 'ticket_number',
      filterType: 'text',
      getValue: (t) => (t.ticket_number ? `${TYPE_PREFIX[t.type] ?? 'TKT'}-${t.ticket_number}` : ''),
      render: (t) =>
        t.ticket_number
          ? createElement(
              'span',
              { className: 'font-mono text-xs text-muted-foreground' },
              `${TYPE_PREFIX[t.type] ?? 'TKT'}-${t.ticket_number}`,
            )
          : createElement('span', { className: 'text-muted-foreground' }, '—'),
    },
    {
      key: 'title',
      label: 'Title',
      sortKey: 'title',
      filterType: 'text',
      isPrimarySearch: true,
      searchPlaceholder: 'Search ticket...',
      getValue: (t) => t.title,
      render: (t) => createElement('span', { className: 'font-medium' }, t.title),
    },
    {
      key: 'type',
      label: 'Type',
      sortKey: 'type',
      filterType: 'select',
      filterOptions: [
        { label: 'Ticket', value: 'Ticket' },
        { label: 'CAPA', value: 'CAPA' },
        { label: 'Client', value: 'Client' },
      ],
      filterAllLabel: 'All Types',
      getValue: (t) => TYPE_LABELS[t.type] ?? t.type,
      render: (t) =>
        createElement('span', { className: 'text-sm capitalize' }, TYPE_LABELS[t.type] ?? t.type),
    },
    {
      key: 'status',
      label: 'Status',
      sortKey: 'status',
      filterType: 'select',
      filterOptions: Object.values(STATUS_LABELS).map((label) => ({ label, value: label })),
      filterAllLabel: 'All Status',
      getValue: (t) => STATUS_LABELS[t.status] ?? t.status,
      render: (t) =>
        createElement('span', { className: 'text-sm' }, STATUS_LABELS[t.status] ?? t.status),
    },
    {
      key: 'priority',
      label: 'Priority',
      sortKey: 'priority',
      filterType: 'select',
      filterOptions: [
        { label: '1', value: '1' },
        { label: '2', value: '2' },
        { label: '3', value: '3' },
        { label: '4', value: '4' },
        { label: '5', value: '5' },
      ],
      filterAllLabel: 'All Priorities',
      getValue: (t) => String(t.priority ?? ''),
      render: (t) => createElement('span', { className: 'text-sm tabular-nums' }, String(t.priority ?? '—')),
    },
    {
      key: 'department',
      label: 'Department',
      sortKey: 'department',
      filterType: 'select',
      filterOptions: departmentOptions,
      filterAllLabel: 'All Departments',
      getValue: (t) => (t.department ? DEPARTMENT_LABELS[t.department] ?? t.department : ''),
      render: (t) =>
        t.department
          ? createElement('span', { className: 'text-sm' }, DEPARTMENT_LABELS[t.department] ?? t.department)
          : createElement('span', { className: 'text-muted-foreground' }, '—'),
    },
    {
      key: 'assignee',
      label: 'Assignee',
      sortKey: 'assignee',
      filterType: 'select',
      filterOptions: assigneeOptions,
      filterAllLabel: 'All Assignees',
      getValue: (t) => getAssigneeName(t),
      render: (t) => {
        const name = getAssigneeName(t);
        const mongoId = getAssigneeId(t);
        return createElement(
          'div',
          { className: 'flex items-center gap-2' },
          createElement(OwnerAvatar, {
            mongoUserId: mongoId,
            name,
            className: 'h-6 w-6',
            fallbackClassName: 'bg-atek-blue text-white text-[10px] font-medium',
          }),
          createElement('span', { className: 'text-sm' }, name),
        );
      },
    },
    {
      key: 'due_date',
      label: 'Due Date',
      sortKey: 'due_date',
      filterType: 'text',
      getValue: (t) => t.due_date ?? '',
      render: (t) =>
        t.due_date
          ? createElement('span', { className: 'text-sm' }, formatDate(t.due_date))
          : createElement('span', { className: 'text-muted-foreground' }, '—'),
    },
    {
      key: 'actions',
      label: '',
      noFilter: true,
      getValue: () => '',
      render: () => createElement(ChevronRight, { className: 'h-4 w-4 text-gray-400' }),
    },
  ];
}

function escapeCSV(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function exportQmsTicketsCSV(
  items: QmsTicket[],
  columns: QmsTicketColumnDef[],
  ctx: QmsTicketRenderContext,
) {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => escapeCSV(c.label)).join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSV(c.getValue(item, ctx))).join(','),
  );
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `qms_tickets_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function copyQmsTicketsToClipboard(
  items: QmsTicket[],
  columns: QmsTicketColumnDef[],
  ctx: QmsTicketRenderContext,
) {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols.map((c) => String(c.getValue(item, ctx) ?? '')).join('\t'),
  );
  return navigator.clipboard.writeText([header, ...rows].join('\n'));
}

export interface UseQmsTicketListTableResult {
  sortKey: string;
  sortDirection: 1 | -1;
  columnFilters: Record<string, string>;
  columnVisibility: Record<string, boolean>;
  columns: QmsTicketColumnDef[];
  visibleColumns: QmsTicketColumnDef[];
  ctx: QmsTicketRenderContext;
  sortedTickets: QmsTicket[];
  totalCount: number;
  isVisible: (key: string) => boolean;
  toggleColumn: (key: string) => void;
  setColumnFilter: (key: string, value: string) => void;
  handleSort: (key: string) => void;
}

export function useQmsTicketListTable(tickets: QmsTicket[]): UseQmsTicketListTableResult {
  const [sortKey, setSortKey] = useState<string>('ticket_number');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(loadColumnFilters);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadVisibility);

  const ctx: QmsTicketRenderContext = useMemo(() => ({ _: undefined }), []);

  const assigneeOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { label: string; value: string }[] = [];
    for (const t of tickets) {
      const name = getAssigneeName(t);
      if (!seen.has(name)) {
        seen.add(name);
        opts.push({ label: name, value: name });
      }
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [tickets]);

  const departmentOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { label: string; value: string }[] = [];
    for (const t of tickets) {
      if (!t.department) continue;
      const label = DEPARTMENT_LABELS[t.department] ?? t.department;
      if (!seen.has(label)) {
        seen.add(label);
        opts.push({ label, value: label });
      }
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [tickets]);

  const columns = useMemo(
    () => buildColumns(assigneeOptions, departmentOptions),
    [assigneeOptions, departmentOptions],
  );

  const isVisible = useCallback(
    (key: string) => columnVisibility[key] !== false,
    [columnVisibility],
  );

  const visibleColumns = useMemo(
    () => columns.filter((col) => columnVisibility[col.key] !== false),
    [columns, columnVisibility],
  );

  const toggleColumn = useCallback((key: string) => {
    setColumnVisibility((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      localStorage.setItem(VISIBILITY_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const setColumnFilter = useCallback((key: string, value: string) => {
    setColumnFilters((prev) => {
      const next = { ...prev, [key]: value };
      localStorage.setItem(FILTERS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const filteredTickets = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return tickets;

    return tickets.filter((ticket) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = columns.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(ticket, ctx);

        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue || ''));
          } catch {
            return true;
          }
        }

        return String(rawValue ?? '').toLowerCase().includes(filterValue.toLowerCase());
      }),
    );
  }, [tickets, columnFilters, columns, ctx]);

  const sortedTickets = useMemo(() => {
    const col = columns.find((c) => c.sortKey === sortKey);
    if (!col) return filteredTickets;

    return [...filteredTickets].sort((a, b) => {
      const aVal = col.getValue(a, ctx);
      const bVal = col.getValue(b, ctx);
      const cmp = aVal.localeCompare(bVal, undefined, { sensitivity: 'base', numeric: true });
      return cmp * sortDirection;
    });
  }, [filteredTickets, sortKey, sortDirection, columns, ctx]);

  const handleSort = useCallback(
    (key: string) => {
      if (sortKey === key) {
        setSortDirection((prev) => (prev === 1 ? -1 : 1));
      } else {
        setSortKey(key);
        setSortDirection(1);
      }
    },
    [sortKey],
  );

  return {
    sortKey,
    sortDirection,
    columnFilters,
    columnVisibility,
    columns,
    visibleColumns,
    ctx,
    sortedTickets,
    totalCount: tickets.length,
    isVisible,
    toggleColumn,
    setColumnFilter,
    handleSort,
  };
}
